import fetchAccountRoles from '@Application/Security/Roles/fetchAccountRoles';
import FetchAccountRoles from '@Application/Security/Roles/Queries/FetchAccountRoles';
import fetchGrantedUsers from '@Application/SubjectTypeDefinitions/fetchGrantedUsers';
import FetchGrantedUsers from '@Application/SubjectTypeDefinitions/Queries/FetchGrantedUsers';
import HintText from '@Guidelines/Component/HintText';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import SectionCard from '@Guidelines/Component/SectionCard';
import type SubjectTypeGrantsFormData from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import type { GrantDraft } from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import AddRoleGrant from '@UI/Pages/Administration/SubjectTypes/Shared/UsableBy/AddRoleGrant';
import AddUserGrant from '@UI/Pages/Administration/SubjectTypes/Shared/UsableBy/AddUserGrant';
import GrantRow from '@UI/Pages/Administration/SubjectTypes/Shared/UsableBy/GrantRow';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type GrantableUserView from '@UI/Store/SubjectTypeDefinitions/Views/GrantableUserView';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Props = {
	readonly grants: SubjectTypeGrantsFormData;
	readonly onChange: (update: (grants: SubjectTypeGrantsFormData) => SubjectTypeGrantsFormData) => void;
	readonly isReadOnly: boolean;
};

const UNKNOWN_ROLE = 'Rôle inconnu ou désactivé';
const UNKNOWN_USER = 'Utilisateur inconnu';
const LOADING_NAME = 'Chargement…';

/** Who may use dossiers of this type: role and user lines with a level, names resolved from the account. */
const UsableBySection: React.FC<Props> = ({ grants, onChange, isReadOnly }) => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const serverMessage = grants.getUsableByServerMessage();

	// The saved lines are named once, on mount; users added afterwards are remembered as they are picked.
	const [savedUserIds] = React.useState(() =>
		grants.usableBy.filter((grant) => grant.kind === 'user').map((grant) => grant.id),
	);
	const [addedUsers, setAddedUsers] = React.useState<readonly GrantableUserView[]>([]);

	const { data: accountRoles } = useStoreQuery({
		selector: (state) => state.rolesStore.accountRoles,
		stopOn: (value) => accountId === null || (value !== null && value.accountId === accountId),
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAccountRoles(new FetchAccountRoles(accountId)));
		},
		deps: [accountId],
	});

	const { data: grantedUsers } = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.grantedUsers,
		stopOn: (value) => accountId === null || (value !== null && value.isFor(accountId, savedUserIds)),
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchGrantedUsers(new FetchGrantedUsers(accountId, savedUserIds)));
		},
		deps: [accountId, savedUserIds],
	});

	const roles = accountRoles?.list ?? null;
	const grantedUserList = grantedUsers?.list ?? null;

	const resolveName = React.useCallback(
		(grant: GrantDraft): string => {
			if (grant.kind === 'role') {
				if (roles === null) {
					return LOADING_NAME;
				}

				const role = roles.find((entry) => entry.id === grant.id && entry.isActive);

				return role?.label ?? UNKNOWN_ROLE;
			}

			const added = addedUsers.find((entry) => entry.id === grant.id);

			if (added !== undefined) {
				return added.displayName;
			}

			if (grantedUserList === null) {
				return LOADING_NAME;
			}

			return grantedUserList.find((entry) => entry.id === grant.id)?.displayName ?? UNKNOWN_USER;
		},
		[roles, grantedUserList, addedUsers],
	);

	const handleAddUser = React.useCallback(
		(addedUser: GrantableUserView, level: string) => {
			setAddedUsers((current) => [...current, addedUser]);
			onChange((current) => current.addGrant('user', addedUser.id, level));
		},
		[onChange],
	);

	return (
		<SectionCard title="Utilisable par">
			{grants.usableBy.map((grant, index) => (
				<GrantRow
					key={`${grant.kind}-${grant.id}`}
					grants={grants}
					onChange={onChange}
					index={index}
					grant={grant}
					name={resolveName(grant)}
					isReadOnly={isReadOnly}
				/>
			))}
			<ConditionalLoader data={serverMessage}>
				{(message) => <small className="ui-kit-field-error">{message}</small>}
			</ConditionalLoader>
			<ConditionalLoader data={roles}>
				{(activeRoles) => (
					<AddRoleGrant
						grants={grants}
						onChange={onChange}
						roles={activeRoles}
						isReadOnly={isReadOnly}
					/>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={accountId}>
				{(currentAccountId) => (
					<AddUserGrant
						grants={grants}
						accountId={currentAccountId}
						onAdd={handleAddUser}
						isReadOnly={isReadOnly}
					/>
				)}
			</ConditionalLoader>
			<HintText>
				Retirer une ligne retire l'accès aux personnes qu'elle couvre, membres des dossiers compris,
				sauf si une autre ligne leur en donne.
			</HintText>
		</SectionCard>
	);
};

export default UsableBySection;
